/**
 * Utility functions for the Action Renderer Registry.
 *
 * These helpers enable looking up registry entries and extracting
 * action metadata without direct registry coupling.
 */

import { isMemoEntityType } from '@/lib/crm/record/notes/notes.utils';
import { STAGE_ASSOCIATION_TYPES_SET } from '@/lib/proposals/stageAssociationTypes';

import type { ProposedAction, TimelineEventPayload } from '../core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
  isObjectCreatedPayload,
  isTimelineEventPayload,
} from '../core/types';
import { ACTION_REGISTRY } from './registry';
import type { ActionMetadata, ActionTypeKey } from './types';

/**
 * Generate the registry key for an action.
 *
 * For top-level action types (gmail_send, calendar_event_create),
 * returns the type name directly.
 *
 * For timeline_event actions, returns a composite key:
 * `timeline_event:{event_type_name}`
 *
 * For actions with action_key sub-routing (pipeline_customization, onboarding_action, etc.),
 * returns a composite key: `{action_type}:{action_key}`
 *
 * Backwards compatibility: external_action with action_key maps to `onboarding_action:{action_key}`
 * to support old proposals.
 *
 * @param action - The action to generate a key for
 * @param stageAssocTypes - Optional set of stage association type names (from useAssociationTypes).
 *   When provided, enables dynamic detection of stage associations for custom entity types.
 *   Falls back to hardcoded STAGE_ASSOCIATION_TYPES_SET when omitted.
 * @returns The registry key, or null if the action type is unknown
 */
export function getActionKey(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): ActionTypeKey | null {
  const { proposed_action_type_name, payload } = action;
  const stageSet = stageAssocTypes ?? STAGE_ASSOCIATION_TYPES_SET;

  // Handle timeline_event with nested event type
  if (proposed_action_type_name === 'timeline_event') {
    if (isTimelineEventPayload(payload)) {
      const entityPayload = payload as TimelineEventPayload;

      // Sub-route actions on hidden entities to HiddenEntityCard — the
      // degraded-render safety net for entities the viewer's user-scoped
      // store can't hydrate (org-user projections, tombstoned external
      // persons, inbound stubs). See HiddenEntityCard's docstring.
      if (action.enriched_data?.is_hidden) {
        return 'timeline_event:hidden_entity_add' as ActionTypeKey;
      }

      // Sub-route stage associations to dedicated component.
      // Unlike the generic timeline_event:{event_type_name} key, stage associations
      // need payload-level inspection because both regular and stage associations
      // share the same event_type_name (ahoy_association_added/removed).
      if (
        entityPayload.event_type_name === 'ahoy_association_added' &&
        isAssociationAddedPayload(entityPayload) &&
        stageSet.has(entityPayload.payload.association_type_name)
      ) {
        return 'timeline_event:stage_association_added' as ActionTypeKey;
      }
      if (
        entityPayload.event_type_name === 'ahoy_association_removed' &&
        isAssociationRemovedPayload(entityPayload) &&
        stageSet.has(entityPayload.payload.association_type_name)
      ) {
        return 'timeline_event:stage_association_removed' as ActionTypeKey;
      }
      const key =
        `timeline_event:${entityPayload.event_type_name}` as ActionTypeKey;
      // Validate key exists in registry
      if (key in ACTION_REGISTRY) {
        return key;
      }
    }
    return null;
  }

  // Handle action types with action_key sub-routing (generic with backwards compat)
  const actionParams = (payload as { action_params?: Record<string, unknown> })
    ?.action_params;
  if (actionParams && typeof actionParams.action_key === 'string') {
    // Backwards compatibility: old external_action proposals should map to onboarding_action composite keys
    const keyPrefix =
      proposed_action_type_name === 'external_action'
        ? 'onboarding_action'
        : proposed_action_type_name;
    const compositeKey =
      `${keyPrefix}:${actionParams.action_key}` as ActionTypeKey;
    if (compositeKey in ACTION_REGISTRY) {
      return compositeKey;
    }
  }

  // Handle top-level action types
  const key = proposed_action_type_name as ActionTypeKey;
  if (key in ACTION_REGISTRY) {
    return key;
  }

  return null;
}

/**
 * Look up the registry entry for an action.
 *
 * @param action - The action to look up
 * @param stageAssocTypes - Optional dynamic stage association types set
 * @returns The registry entry, or null if not found
 */
export function lookupAction(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
) {
  const key = getActionKey(action, stageAssocTypes);
  if (!key) {
    return null;
  }
  return ACTION_REGISTRY[key];
}

/**
 * Get metadata for an action without its component.
 *
 * This is useful for displaying action info in headers, lists, etc.
 * without actually rendering the full component.
 *
 * @param action - The action to get metadata for
 * @param stageAssocTypes - Optional dynamic stage association types set
 * @returns The action metadata, or null if not found
 */
export function getActionMetadata(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): ActionMetadata | null {
  const entry = lookupAction(action, stageAssocTypes);
  if (!entry) {
    return null;
  }

  return {
    title: entry.getTitle(action.payload, action),
    category: entry.category,
    icon: entry.icon,
    supportsEditing: entry.supportsEditing,
    isDestructive: entry.isDestructive,
  };
}

/**
 * Check if an action type is supported by the registry.
 *
 * @param action - The action to check
 * @param stageAssocTypes - Optional dynamic stage association types set
 * @returns True if the action type is supported
 */
export function isActionTypeSupported(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): boolean {
  return getActionKey(action, stageAssocTypes) !== null;
}

/**
 * Get all registered action type keys.
 *
 * @returns Array of all registered action type keys
 */
export function getRegisteredActionTypes(): ActionTypeKey[] {
  return Object.keys(ACTION_REGISTRY) as ActionTypeKey[];
}

/**
 * Whether a hidden-entity-add action has an editable underlying payload.
 *
 * Hidden-entity proposals cover several underlying event types (association
 * added/removed, property updates, object creates). Today we only support
 * editing when the underlying event is a stage association — the user can
 * change the target stage before approving. Other subtypes have no inline
 * edit form yet (see GitHub issue #4307 for full-contact-editing follow-up).
 */
function isEditableHiddenEntityPayload(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): boolean {
  if (!isTimelineEventPayload(action.payload)) {
    return false;
  }
  const stageSet = stageAssocTypes ?? STAGE_ASSOCIATION_TYPES_SET;
  const payload = action.payload as TimelineEventPayload;
  if (
    payload.event_type_name === 'ahoy_association_added' &&
    isAssociationAddedPayload(payload)
  ) {
    return stageSet.has(payload.payload.association_type_name);
  }
  return false;
}

/**
 * Whether the user can edit this action AT ALL, by any affordance.
 *
 * This is the union of the shell edit button and the affordances cards own
 * themselves — the Gmail compose card, `ask_user_for_info`'s inline form,
 * UpdateActionCard's click-to-edit cells, NoteCard's pencil. Ask this when the
 * question is "can the user change this action?" rather than "does the shell
 * render a pencil?"; the two answers differ for every card-owned affordance.
 *
 * Only two things make an action genuinely uneditable:
 *   - the registry entry has no edit support at all (`supportsEditing`);
 *   - the entry supports editing for some subcases but not this one — risk
 *     creates are informational and have no form, and hidden-entity adds only
 *     have one for the stage subcase (see `isEditableHiddenEntityPayload`).
 *
 * Runtime state like `isProcessing` stays in the consumer component — this
 * helper only considers the action's static shape.
 */
export function isUserEditableAction(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): boolean {
  const key = getActionKey(action, stageAssocTypes);
  return isUserEditableActionForKey(action, key, stageAssocTypes);
}

/**
 * Whether an action is editable via THE SHELL EDIT BUTTON specifically.
 *
 * `isUserEditableAction` minus the cases where the card renders its own edit
 * control, so a shell button would be a second, competing affordance:
 *   - `gmail_send` self-manages editing inside the Gmail compose card.
 *   - `ask_user_for_info` is edited inline by default (its form fields autosave
 *     via the update_payload rail), so the shell edit button is a redundant
 *     double affordance and is hidden — same rationale as gmail_send.
 *   - `timeline_event:ahoy_property_updated` is click-to-edit and autosaves on
 *     commit (UpdateActionCard). A shell edit session would add a second dirty
 *     state and a Save button that can disagree with the autosave about whether
 *     anything is outstanding, so it is hidden — same rationale as
 *     ask_user_for_info.
 *   - Memo creates (`ahoy_note` / `ahoy_task`) render as a NoteCard that owns
 *     its own inline edit pencil and save rail, so a shell button is a
 *     duplicate affordance and is hidden — same rationale as gmail_send.
 *
 * Use this ONLY to decide whether to render that button. Anything reasoning
 * about whether the user can change the action — such as telling them editing
 * a failed action is worth a try — wants `isUserEditableAction`; using this one
 * there silently treats every card-owned affordance as uneditable.
 */
export function isEditableAction(
  action: ProposedAction,
  stageAssocTypes?: Set<string>
): boolean {
  const key = getActionKey(action, stageAssocTypes);
  if (!isUserEditableActionForKey(action, key, stageAssocTypes)) {
    return false;
  }
  if (key === 'gmail_send') {
    return false;
  }
  if (key === 'ask_user_for_info') {
    return false;
  }
  if (key === 'timeline_event:ahoy_property_updated') {
    return false;
  }
  if (
    isObjectCreatedPayload(action.payload) &&
    isMemoEntityType(action.payload.entity_type_name)
  ) {
    return false;
  }
  return true;
}

/**
 * Shared implementation behind `isUserEditableAction`, taking the action's
 * already-computed registry key so callers that also need the key for their
 * own routing (like `isEditableAction`) don't have to look it up twice.
 */
function isUserEditableActionForKey(
  action: ProposedAction,
  key: ActionTypeKey | null,
  stageAssocTypes?: Set<string>
): boolean {
  const entry = key ? ACTION_REGISTRY[key] : null;
  if (!entry?.supportsEditing) {
    return false;
  }
  if (
    isObjectCreatedPayload(action.payload) &&
    action.payload.entity_type_name === 'ahoy_risk'
  ) {
    return false;
  }
  if (key === 'timeline_event:hidden_entity_add') {
    return isEditableHiddenEntityPayload(action, stageAssocTypes);
  }
  return true;
}
